import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';

import AuthenticatedApp from './components/AuthenticatedApp';
import VoxTestsFlow from './components/VoxTestsFlow';
import VoxTestsAdmin from './components/VoxTestsAdmin';
import FirebaseAuth from './components/FirebaseAuth';
import VoyxTimeHome from './components/VoyxTimeHome';
import LandingPage from './components/LandingPage';
import Privacy from './components/Privacy';
import About from './components/About';
import Contact from './components/Contact';
import SelflessCall from './components/SelflessCall';
import {
  shouldBypassAuth,
  extractRouteParams,
  setupExternalTestSession,
  logExternalAccess
} from './utils/routingUtils';

import './App.css';

/**
 * Loading animation — horn torus wireframe with progressive vertical reveal
 * Ported from iOS TorusRenderer.swift startLoading/updateLoadingProgress
 */
const TorusLoader = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    const size = Math.max(window.innerWidth, window.innerHeight);
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.set(0, -3, -3);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(size, size);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x050505);
    container.appendChild(renderer.domElement);

    const segments = 42;
    const R = 0.19;
    let phaseOffset = 0;
    let progressCount = 0;
    let progressTimer = 0;
    // Per-vertical brightness (0 = dim, 1 = fully lit) for smooth fade
    const verticalBrightness = new Float32Array(segments + 1).fill(0);
    const torusGroup = new THREE.Group();
    scene.add(torusGroup);

    const buildGeometry = (brightness, phase) => {
      const positions = [];
      const colors = [];
      const indices = [];
      const stride = segments + 1;
      const dimLevel = 0.12;
      const litLevel = 0.9;

      for (let i = 0; i <= segments; i++) {
        const phi = (i / segments) * 2 * Math.PI - phase;
        for (let j = 0; j <= segments; j++) {
          const omega = (j / segments) * 2 * Math.PI + phase;
          const x = R * (1 - Math.cos(phi)) * Math.sin(omega);
          const y = R * (1 - Math.cos(phi)) * Math.cos(omega);
          const z = R * Math.sin(phi);
          positions.push(x, y, z);

          const b = dimLevel + (litLevel - dimLevel) * brightness[j];
          colors.push(b, b, b);
        }
      }

      // Vertical lines (meridians)
      for (let j = 0; j <= segments; j++) {
        for (let i = 0; i < segments; i++) {
          indices.push(i * stride + j, (i + 1) * stride + j);
        }
      }
      // Horizontal lines (rings)
      for (let i = 0; i <= segments; i++) {
        for (let j = 0; j < segments; j++) {
          indices.push(i * stride + j, i * stride + j + 1);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geometry.setIndex(indices);
      return geometry;
    };

    const material = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.6 });
    let mesh = new THREE.LineSegments(buildGeometry(verticalBrightness, 0), material);
    torusGroup.add(mesh);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      phaseOffset += 0.016 * 0.1;
      progressTimer += 0.016;
      if (progressTimer > 0.08) {
        progressTimer = 0;
        progressCount++;
        if (progressCount > segments) progressCount = 0;
      }
      // Light up current vertical, fade all others
      for (let j = 0; j <= segments; j++) {
        if (j < progressCount) {
          // Ramp up quickly
          verticalBrightness[j] = Math.min(verticalBrightness[j] + 0.15, 1);
        } else {
          // Fade out smoothly
          verticalBrightness[j] = Math.max(verticalBrightness[j] - 0.03, 0);
        }
      }
      const oldGeo = mesh.geometry;
      mesh.geometry = buildGeometry(verticalBrightness, phaseOffset);
      oldGeo.dispose();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      mesh.geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={containerRef} className="loading-container" />;
};

/**
 * Composant de route pour les appels Selfless
 * Route: /call/:roomId
 */
const CallRoute = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();

  if (!roomId) {
    return (
      <div className="error-container">
        <h2>Room ID manquant</h2>
        <p>Veuillez fournir un identifiant de room valide.</p>
        <button onClick={() => navigate('/')}>Retour à l'accueil</button>
      </div>
    );
  }

  return <SelflessCall roomId={roomId} onClose={() => navigate('/')} />;
};

/**
 * Composant de route pour les tests externes
 * Route: /record/:btId/:password
 */
const ExternalTestRoute = () => {
  const { btId, password } = useParams();
  const [sessionResult, setSessionResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        // Valider les paramètres et configurer la session
        const result = setupExternalTestSession(btId, password);

        if (!result.success) {
          setError(result.error);
          setIsLoading(false);
          return;
        }

        setSessionResult(result);
        setIsLoading(false);

      } catch (err) {
        console.error('Erreur initialisation session externe:', err);
        setError('Erreur lors de l\'initialisation de la session');
        setIsLoading(false);
      }
    };

    if (btId && password) {
      initializeSession();
    } else {
      setError('Paramètres manquants');
      setIsLoading(false);
    }
  }, [btId, password]);

  const handleComplete = (diagnosticData) => {
    console.log('🎯 Test externe terminé:', diagnosticData);
    // Ici on pourrait rediriger ou afficher un message de confirmation
    alert('Test vocal terminé. Merci pour votre participation !');
  };

  const handleError = (error) => {
    console.error('❌ Erreur test externe:', error);
    setError(error.message || 'Une erreur est survenue');
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Initialisation de la session de test...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Accès non autorisé</h2>
        <p>{error}</p>
        <div className="error-details">
          <p><strong>BT ID:</strong> {btId}</p>
          <p>Veuillez vérifier vos identifiants d'accès.</p>
        </div>
      </div>
    );
  }

  if (sessionResult?.success) {
    return (
      <VoxTestsFlow
        user={sessionResult.user}
        onComplete={handleComplete}
        onError={handleError}
        onClose={() => handleComplete({})}
        isOverlay={true}
      />
    );
  }

  return null;
};

/**
 * Demo entry gate — forced loading animation before the experience
 */
const DemoGate = ({ user, setUser }) => {
  const [phase, setPhase] = useState('loading'); // 'loading' | 'fading' | 'ready'

  useEffect(() => {
    const loadTimer = setTimeout(() => setPhase('fading'), 4200);
    return () => clearTimeout(loadTimer);
  }, []);

  useEffect(() => {
    if (phase === 'fading') {
      const fadeTimer = setTimeout(() => setPhase('ready'), 800);
      return () => clearTimeout(fadeTimer);
    }
  }, [phase]);

  if (phase === 'ready') return <AuthenticatedApp user={user} setUser={setUser} />;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <div style={{
        opacity: phase === 'fading' ? 0 : 1,
        transition: 'opacity 0.8s ease-out',
        position: 'absolute',
        inset: 0,
        zIndex: 1
      }}>
        <TorusLoader />
      </div>
      <div style={{
        opacity: phase === 'fading' ? 1 : 0,
        transition: 'opacity 0.8s ease-in',
        position: 'absolute',
        inset: 0,
        zIndex: 0
      }}>
        <AuthenticatedApp user={user} setUser={setUser} />
      </div>
    </div>
  );
};

/**
 * Composant principal App avec routing
 */
const App = () => {
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const location = useLocation();

  // Gestion de l'authentification Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Bypass d'authentification pour localhost et routes spéciales
  useEffect(() => {
    if (!user && !isAuthLoading) {
      // Bypass localhost comme dans l'ancienne version
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        // Seulement si on n'est pas sur une route externe
        // ET si on n'est pas sur la racine (Landing Page) ou login
        const isPublicRoute = ['/', '/login', '/privacy', '/about', '/contact'].includes(location.pathname);

        if (!shouldBypassAuth(location.pathname) && !isPublicRoute) {
          setUser({
            uid: 'local-test-user',
            email: 'test@localhost.com',
            displayName: 'Test User',
            isLocalUser: true
          });
        }
      }
    }
  }, [user, isAuthLoading, location.pathname]);

  // Routes qui n'ont pas besoin d'authentification Firebase
  const isExternalRoute = shouldBypassAuth(location.pathname);

  if (isAuthLoading && !isExternalRoute) {
    return <TorusLoader />;
  }

  return (
    <>

      <Routes>
        {/* Landing Page — toujours accessible */}
        <Route
          path="/"
          element={<LandingPage />}
        />

        {/* App authentifiée — route dédiée */}
        <Route
          path="/app"
          element={
            user ? (
              <DemoGate user={user} setUser={setUser} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Route de connexion — redirige vers /app si déjà connecté */}
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to="/app" replace />
            ) : (
              <FirebaseAuth onAuth={setUser} />
            )
          }
        />

        {/* Route pour les tests externes */}
        <Route
          path="/record/:btId/:password"
          element={<ExternalTestRoute />}
        />

        {/* Route admin VoxTests */}
        <Route
          path="/admin/voxtests"
          element={
            user ? (
              // Vérifier si l'utilisateur a accès à l'admin
              user.email === 'delaitrejacques4@gmail.com' ||
                user.email === 'alex@ichai.co' ||
                user.email === 'Alex@ichai.co' ||
                user.isLocalUser ? (
                <VoxTestsAdmin user={user} onClose={() => window.location.href = '/'} />
              ) : (
                <div className="access-denied">
                  <h2>Accès refusé</h2>
                  <p>Vous n'avez pas les permissions pour accéder à cette page.</p>
                  <p>Email connecté: "{user.email}"</p>
                  <p>Debug: Longueur email = {user.email?.length}</p>
                  <button onClick={() => window.location.href = '/'}>Retour</button>
                </div>
              )
            ) : (
              <FirebaseAuth onAuth={setUser} />
            )
          }
        />

        {/* Routes VoyxTime */}
        <Route
          path="/voyxtime"
          element={<VoyxTimeHome />}
        />

        <Route
          path="/call/:roomId"
          element={<CallRoute />}
        />

        {/* Pages publiques */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />

        {/* Route de santé pour monitoring */}
        <Route
          path="/health"
          element={
            <div className="health-check">
              <h2>Service Status</h2>
              <p>✅ Résonance Vocale - Service actif</p>
              <p>Timestamp: {new Date().toISOString()}</p>
            </div>
          }
        />

        {/* Route 404 */}
        <Route
          path="*"
          element={
            <div className="not-found-container">
              <h2>Page non trouvée</h2>
              <p>La page que vous cherchez n'existe pas.</p>
              <a href="/">Retour à l'accueil</a>
            </div>
          }
        />
      </Routes>
    </>
  );
};

export default App;